const { debounce, getElement, viewportSize } = window.BAO.Utils

class HeaderDropdown extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.offsetCalculated = false
    this.calculateOffset()
  }

  setupListeners () {
    super.setupListeners()

    this.measureDropdownHeights = this.measureDropdownHeights.bind(this)

    if (this.els?.['menu-dropdown-toggle']?.elements) {
      this.els['menu-dropdown-toggle'].elements.forEach(toggle => {
        toggle.addEventListener(
          'click',
          this.handleToggleButtonClick.bind(this),
        )
      })
    }

    document.addEventListener('focusin', this.handleFocusIn.bind(this))
    document.addEventListener('keyup', this.handleEscapePress.bind(this))

    this.debouncedMeasure = debounce(
      this.measureDropdownHeights.bind(this),
      200,
    )
    window.addEventListener('resize', () => {
      this.debouncedMeasure()
      this.calculateOffset()
    })

    document.addEventListener('DOMContentLoaded', this.measureDropdownHeights)
    document.addEventListener(
      'shopify:section:load',
      this.measureDropdownHeights,
    )
  }

  /**
   * @param {ClickEvent} event
   *
   * @returns {void}
   */
  handleToggleButtonClick (event) {
    if (!this.els?.['menu-dropdown-toggle']?.exists) return

    if (!event.target.closest(this.els?.['menu-dropdown-toggle'].selector)) {
      return
    }

    const menuToggleEl = event.target
    const parentItemEl = menuToggleEl.closest(this.els?.item?.selector)
    if (!parentItemEl) return
    const menuDropdownEl = getElement(this.els?.['menu-dropdown']?.selector, {
      context: parentItemEl,
    })

    if (!menuDropdownEl) return

    const isExpanded = menuDropdownEl.getAttribute('aria-expanded') === 'true'

    if (isExpanded) {
      menuToggleEl.removeAttribute('data-active')
      menuDropdownEl.removeAttribute('aria-expanded')
    } else {
      menuToggleEl.setAttribute('data-active', 'true')
      menuDropdownEl.setAttribute('aria-expanded', 'true')
    }
  }

  /**
   * @param {FocusInEvent} event
   *
   * @returns {void}
   */
  handleFocusIn (event) {
    if (
      event.target.closest(this.localName) &&
      !event.target.closest('[aria-expanded="true"]')
    ) {
      /* This ensures an expanded dropdown closes when the user tabs out of it but focus remains within the header-dropdown element */
      this.closeDropdowns()
    }

    if (!event.target.closest(this.localName)) {
      /* This ensures all dropdowns are closed when focus leaves the header-dropdown element */
      this.closeDropdowns()
    }
  }

  /**
   * @returns {void}
   */
  measureDropdownHeights () {
    this.style.removeProperty('--Dropdown_MinHeight')

    const dropdowns = this.els?.['menu-dropdown-inner']?.elements || []

    if (dropdowns.length === 0) return

    const heights = dropdowns.map(el => el.getBoundingClientRect().height)

    const minHeight = Math.max(...heights)

    this.style.setProperty('--Dropdown_MinHeight', `${minHeight}px`)
  }

  /**
   * @param {KeyboardEvent} event
   *
   * @returns {void}
   */
  handleEscapePress (event) {
    if (event.code?.toUpperCase() !== 'ESCAPE') return
    this.closeDropdowns()
  }

  /**
   * @returns {void}
   */
  closeDropdowns () {
    if (this.els?.['menu-dropdown-toggle']?.exists) {
      this.els?.['menu-dropdown-toggle']?.elements.forEach(menuDropdownEl => {
        menuDropdownEl.removeAttribute('data-active')
      })
    }

    if (this.els?.['menu-dropdown']?.exists) {
      this.els?.['menu-dropdown']?.elements.forEach(menuDropdownEl => {
        menuDropdownEl.removeAttribute('aria-expanded')
      })
    }
  }

  calculateOffset () {
    if (!this.els?.item?.exists) return
    if (this.offsetCalculated) return
    if (viewportSize() !== 'lg' && viewportSize() !== 'xl') return

    this.els.item.elements.forEach(itemEl => {
      if (itemEl.dataset.useOffset !== 'true') return

      let totalWidth = 0
      let current = itemEl.previousElementSibling

      while (current) {
        totalWidth += current.offsetWidth
        current = current.previousElementSibling
      }

      itemEl.classList.add('hd-Banner_Item-offset')
      itemEl.style.setProperty('--HeaderDropdown_ItemOffset', `${totalWidth}px`)

      this.offsetCalculated = true
    })
  }
}

if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', HeaderDropdown)
}
